// src/pages/Signup.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Container,
    FormControl,
    FormLabel,
    Heading,
    Input,
    Stack,
    Text,
    useToast,
    InputGroup,
    InputRightElement,
    Flex,
    Image,
    Link,
    Divider,
    HStack,
} from '@chakra-ui/react';
import { supabase } from '../config/supabaseClient';
import logo from '../assets/logo.png';
import { FcGoogle } from 'react-icons/fc';
import { FiAlertCircle } from 'react-icons/fi'; // Adicione esta linha

// Importe o PhoneInput e o estilo necessário
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css'; // Importe o estilo padrão
import '../styles/PhoneInput.css'; // Seu estilo customizado

// Importe o InputMask para formatação de CPF/CNPJ
import InputMask from 'react-input-mask';

import { cpf, cnpj } from 'cpf-cnpj-validator';

const Signup = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        cpf_cnpj: '',
        password: '',
        confirmPassword: '',
        postal_code: '',
        address: '',
        address_number: '',
        city: '',
        state: '',
        neighborhood: '',
        complement: ''
    });
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [googleLoading, setGoogleLoading] = useState(false);
    const [isCpf, setIsCpf] = useState(true); // Controla se é CPF (true) ou CNPJ (false)

    const navigate = useNavigate();
    const toast = useToast();
    const [loadingCep, setLoadingCep] = useState(false);

    const [formValid, setFormValid] = useState(false);

    // Passo 3: Adicione o useEffect para validação em tempo real
    useEffect(() => {
        // Verificar se os campos obrigatórios estão preenchidos
        const requiredFields = formData.name && formData.email && formData.password &&
            formData.confirmPassword && formData.phone && formData.cpf_cnpj;

        // Verificar se as senhas conferem
        //const passwordsMatch = formData.password === formData.confirmPassword;

        // Verificar email 
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const emailValid = emailRegex.test(formData.email);

        // Verificar documento (CPF/CNPJ)
        const documentValid = isCpf
            ? cpf.isValid(formData.cpf_cnpj)
            : cnpj.isValid(formData.cpf_cnpj);

        // Verificar se o número do endereço está preenchido quando há CEP
        const addressValid = !formData.postal_code ||
            formData.postal_code.replace(/\D/g, '').length !== 8 ||
            !!formData.address_number;

        // Atualizar o estado de validade do formulário
        setFormValid(requiredFields && emailValid && documentValid && addressValid);
    }, [formData, isCpf]);


    // Substituir o método handleSearchCep no Signup.jsx
    const handleSearchCep = async () => {
        const cep = formData.postal_code?.replace(/\D/g, '');

        if (!cep || cep.length !== 8) {
            toast({
                title: 'CEP inválido',
                description: 'Por favor, digite um CEP válido com 8 dígitos.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setLoadingCep(true);

        try {
            const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
            const data = await response.json();

            if (data.erro) {
                toast({
                    title: 'CEP não encontrado',
                    description: 'O CEP informado não foi encontrado.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
                return;
            }

            setFormData(prev => ({
                ...prev,
                address: data.logradouro,
                neighborhood: data.bairro,
                city: data.localidade,
                state: data.uf,
                // Mantém outros campos inalterados
            }));

            toast({
                title: 'Endereço encontrado',
                description: 'Os dados de endereço foram preenchidos automaticamente.',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            console.error('Erro ao buscar CEP:', error);
            toast({
                title: 'Erro na busca',
                description: 'Ocorreu um erro ao buscar o CEP. Tente novamente.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setLoadingCep(false);
        }
    };

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const toggleDocumentType = () => {
        setIsCpf(!isCpf);
        setFormData({
            ...formData,
            cpf_cnpj: '' // Limpa o campo quando troca o tipo
        });
    };

    const validateForm = () => {
        if (!formData.name || !formData.email || !formData.password || !formData.phone || !formData.cpf_cnpj) {
            toast({
                title: 'Campos obrigatórios',
                description: 'Por favor, preencha todos os campos.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        if (formData.password !== formData.confirmPassword) {
            toast({
                title: 'Senhas não conferem',
                description: 'As senhas não estão iguais.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        // Validação básica de email
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            toast({
                title: 'Email inválido',
                description: 'Por favor, digite um email válido.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        // Validação de CPF/CNPJ usando a biblioteca
        const isDocumentValid = isCpf
            ? cpf.isValid(formData.cpf_cnpj)
            : cnpj.isValid(formData.cpf_cnpj);

        if (!isDocumentValid) {
            toast({
                title: isCpf ? 'CPF inválido' : 'CNPJ inválido',
                description: `Por favor, digite um ${isCpf ? 'CPF' : 'CNPJ'} válido.`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        // Se tiver o CEP preenchido, validar número
        if (formData.postal_code && formData.postal_code.replace(/\D/g, '').length === 8 && !formData.address_number) {
            toast({
                title: 'Número do endereço obrigatório',
                description: 'Se você preencheu o CEP, o número do endereço é obrigatório.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return false;
        }

        return true;
    };

    // src/pages/Signup.jsx - Modificar a função handleSubmit

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        let phoneNumber = formData.phone;
        if (phoneNumber && phoneNumber.startsWith('+')) {
            // Remove o código do país (ex: +55)
            phoneNumber = phoneNumber.substring(phoneNumber.indexOf(' ') + 1);
        }

        setLoading(true);

        try {
            // 1. Criar o usuário na autenticação do Supabase
            const { data: authData, error: authError } = await supabase.auth.signUp({
                email: formData.email,
                password: formData.password,
                options: {
                    data: {
                        name: formData.name,
                        phone: phoneNumber // Agora sem o código do país
                    },
                    emailRedirectTo: 'https://drawgoods.com/auth-redirect'
                }
            });

            if (authError) throw authError;

            // 2. Inserir diretamente o perfil com CPF/CNPJ e demais dados
            // Importante: Não confiar apenas no trigger RLS
            const { error: profileError } = await supabase
                .from('profiles')
                .insert({
                    id: authData.user.id,
                    name: formData.name,
                    email: formData.email,
                    phone: formData.phone, // Manter o formato completo com código do país
                    cpf_cnpj: formData.cpf_cnpj,
                    postal_code: formData.postal_code,
                    address: formData.address,
                    address_number: formData.address_number,
                    city: formData.city,
                    state: formData.state,
                    neighborhood: formData.neighborhood,
                    complement: formData.complement,
                    creditos: 0,
                    comissao_total: 0,
                    comissao_disponivel: 0,
                    cupom_used: false,
                    provider: 'email'
                });

            if (profileError) {
                console.error('Erro ao criar perfil:', profileError);
                // Se falhar a criação do perfil, tentar atualizar
                const { error: updateError } = await supabase
                    .from('profiles')
                    .update({
                        cpf_cnpj: formData.cpf_cnpj,
                        phone: formData.phone,
                        postal_code: formData.postal_code,
                        address: formData.address,
                        address_number: formData.address_number,
                        city: formData.city,
                        state: formData.state,
                        neighborhood: formData.neighborhood,
                        complement: formData.complement
                    })
                    .eq('id', authData.user.id);

                if (updateError) {
                    console.warn('Erro ao atualizar perfil:', updateError);
                    // Não interrompe o processo, pois o usuário pode atualizar depois
                }
            }

            toast({
                title: 'Conta criada com sucesso!',
                description: 'Verifique seu email para confirmar o cadastro.',
                status: 'success',
                duration: 5000,
                isClosable: true,
            });

            // Redirecionar para dashboard ou página de verificação
            navigate('/verification');

        } catch (error) {
            console.error('Erro no cadastro:', error);
            toast({
                title: 'Erro no cadastro',
                description: error.message || 'Ocorreu um erro ao criar sua conta.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSignUp = async () => {
        try {
            setGoogleLoading(true);

            const { error } = await supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: window.location.origin + '/auth-redirect'
                }
            });

            if (error) throw error;

            // Não precisa de redirecionamento aqui, o Supabase gerencia isso
        } catch (error) {
            console.error('Erro ao se cadastrar com Google:', error);
            toast({
                title: 'Erro ao fazer cadastro',
                description: error.message || 'Ocorreu um erro ao tentar se cadastrar com o Google. Tente novamente.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            setGoogleLoading(false);
        }
    };

    return (
        <Container maxW="md" py={12}>
            <Flex direction="column" align="center" mb={8}>
                <Image src={logo} alt="Draw Your Goods" height="60px" mb={4} />
                <Heading size="lg" textAlign="center" color="brand.primary">
                    Crie sua conta no Draw Your Goods
                </Heading>
                <Text mt={2} textAlign="center" color="gray.600">
                    Transforme suas memórias em arte para colorir
                </Text>
            </Flex>

            <Box p={8} borderRadius="lg" boxShadow="md" bg="white">
                {/* Botão de cadastro com Google */}
                <Button
                    w="100%"
                    variant="outline"
                    leftIcon={<FcGoogle size={20} />}
                    onClick={handleGoogleSignUp}
                    mb={6}
                    isLoading={googleLoading}
                    loadingText="Conectando"
                    height="48px"
                    fontSize="md"
                    iconSpacing={3}
                >
                    Cadastrar com Google
                </Button>

                <HStack my={6}>
                    <Divider />
                    <Text fontSize="sm" color="gray.500" whiteSpace="nowrap">ou com email</Text>
                    <Divider />
                </HStack>

                <form onSubmit={handleSubmit}>
                    <Stack spacing={4}>
                        <FormControl id="name" isRequired>
                            <FormLabel>Nome completo</FormLabel>
                            <Input
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                type="text"
                            />
                        </FormControl>

                        <FormControl id="email" isRequired>
                            <FormLabel>Email</FormLabel>
                            <Input
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                type="email"
                            />
                        </FormControl>

                        <FormControl id="phone" isRequired>
                            <FormLabel>Telefone (WhatsApp)</FormLabel>
                            <Box className="phone-input-container">
                                <PhoneInput
                                    international
                                    defaultCountry="BR"
                                    value={formData.phone}
                                    onChange={(value) => setFormData({
                                        ...formData,
                                        phone: value
                                    })}
                                    placeholder="(xx) xxxxx-xxxx"
                                />
                            </Box>
                        </FormControl>

                        <FormControl id="cpf_cnpj" isRequired>
                            <Flex justify="space-between" align="center">
                                <FormLabel>{isCpf ? 'CPF' : 'CNPJ'}</FormLabel>
                                <Button size="xs" onClick={toggleDocumentType} variant="outline">
                                    Mudar para {isCpf ? 'CNPJ' : 'CPF'}
                                </Button>
                            </Flex>
                            <Input
                                as={InputMask}
                                mask={isCpf ? "999.999.999-99" : "99.999.999/9999-99"}
                                name="cpf_cnpj"
                                value={formData.cpf_cnpj}
                                onChange={handleChange}
                                placeholder={isCpf ? "000.000.000-00" : "00.000.000/0000-00"}
                            />
                        </FormControl>

                        <FormControl id="postal_code">
                            <FormLabel>CEP (opcional)</FormLabel>
                            <InputGroup>
                                <Input
                                    as={InputMask}
                                    mask="99999-999"
                                    name="postal_code"
                                    value={formData.postal_code}
                                    onChange={handleChange}
                                    placeholder="00000-000"
                                />
                                <InputRightElement width="4.5rem">
                                    <Button
                                        h="1.75rem"
                                        size="sm"
                                        onClick={handleSearchCep}
                                        isLoading={loadingCep}
                                        isDisabled={!formData.postal_code || formData.postal_code.replace(/\D/g, '').length !== 8}
                                    >
                                        Buscar
                                    </Button>
                                </InputRightElement>
                            </InputGroup>
                        </FormControl>

                        {formData.postal_code && formData.postal_code.replace(/\D/g, '').length === 8 && (
                            <>
                                <FormControl id="address">
                                    <FormLabel>Logradouro</FormLabel>
                                    <Input
                                        name="address"
                                        value={formData.address}
                                        onChange={handleChange}
                                        placeholder="Rua, Avenida, etc."
                                    />
                                </FormControl>

                                <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                                    <FormControl id="address_number">
                                        <FormLabel>Número</FormLabel>
                                        <Input
                                            name="address_number"
                                            value={formData.address_number}
                                            onChange={handleChange}
                                            placeholder="123"
                                        />
                                    </FormControl>

                                    <FormControl id="complement">
                                        <FormLabel>Complemento</FormLabel>
                                        <Input
                                            name="complement"
                                            value={formData.complement}
                                            onChange={handleChange}
                                            placeholder="Apto, Bloco, etc."
                                        />
                                    </FormControl>
                                </Stack>

                                <FormControl id="neighborhood">
                                    <FormLabel>Bairro</FormLabel>
                                    <Input
                                        name="neighborhood"
                                        value={formData.neighborhood}
                                        onChange={handleChange}
                                        placeholder="Bairro"
                                    />
                                </FormControl>

                                <Stack direction={{ base: "column", md: "row" }} spacing={4}>
                                    <FormControl id="city">
                                        <FormLabel>Cidade</FormLabel>
                                        <Input
                                            name="city"
                                            value={formData.city}
                                            onChange={handleChange}
                                            placeholder="Cidade"
                                        />
                                    </FormControl>

                                    <FormControl id="state">
                                        <FormLabel>Estado</FormLabel>
                                        <Input
                                            name="state"
                                            value={formData.state}
                                            onChange={handleChange}
                                            placeholder="UF"
                                            maxLength={2}
                                        />
                                    </FormControl>
                                </Stack>
                            </>
                        )}

                        <FormControl id="password" isRequired>
                            <FormLabel>Senha</FormLabel>
                            <InputGroup>
                                <Input
                                    name="password"
                                    value={formData.password}
                                    onChange={handleChange}
                                    type={showPassword ? 'text' : 'password'}
                                />
                                <InputRightElement width="4.5rem">
                                    <Button
                                        h="1.75rem"
                                        size="sm"
                                        onClick={() => setShowPassword(!showPassword)}
                                    >
                                        {showPassword ? 'Ocultar' : 'Mostrar'}
                                    </Button>
                                </InputRightElement>
                            </InputGroup>
                        </FormControl>

                        <FormControl id="confirmPassword" isRequired>
                            <FormLabel>Confirmar Senha</FormLabel>
                            <Input
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleChange}
                                type="password"
                            />
                        </FormControl>

                        <Button
                            type="submit"
                            colorScheme={formValid ? "blue" : "gray"}
                            bgGradient={formValid
                                ? "linear(to-r, brand.primary, brand.secondary)"
                                : "linear(to-r, gray.300, gray.400)"}
                            color="white"
                            size="lg"
                            w="100%"
                            mt={6}
                            isLoading={loading}
                            loadingText="Criando conta..."
                            disabled={!formValid || loading}
                            opacity={formValid ? 1 : 0.7}
                            _hover={formValid
                                ? {
                                    transform: 'translateY(-2px)',
                                    boxShadow: 'lg',
                                }
                                : {}
                            }
                            _disabled={{
                                cursor: 'not-allowed',
                            }}
                            position="relative"
                            transition="all 0.3s ease"
                        >
                            {!formValid ? (
                                <>
                                    <Box
                                        position="absolute"
                                        top="0"
                                        left="0"
                                        right="0"
                                        bottom="0"
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="center"
                                        bg="blackAlpha.200"
                                        borderRadius="md"
                                    >
                                        <HStack spacing={2}>
                                            <FiAlertCircle />
                                            <Text>Preencha todos os campos</Text>
                                        </HStack>
                                    </Box>
                                    <Text opacity={0}>Criar conta</Text>
                                </>
                            ) : (
                                "Criar conta"
                            )}
                        </Button>

                        {/* Texto explicativo abaixo do botão */}
                        {!formValid && (
                            <Text
                                fontSize="sm"
                                color="gray.500"
                                textAlign="center"
                                mt={2}
                            >
                                Complete todas as informações necessárias para criar sua conta
                            </Text>
                        )}
                    </Stack>
                </form>

                <Text mt={6} textAlign="center">
                    Já tem uma conta?{' '}
                    <Link color="brand.primary" href="/login">
                        Faça login
                    </Link>
                </Text>
            </Box>
        </Container>
    );
};

export default Signup;